import authzInstance from "../authzInstance";

/* =====================
   TYPES  (authz-api /auth/me/*)
===================== */

export interface Account {
  user_id: string;
  username: string;
  login_names: string[];
  first_name: string;
  last_name: string;
  display_name: string;
  nick_name: string;
  preferred_language: string;
  email: string | null;
  email_verified: boolean;
  phone: string | null;
  phone_verified: boolean;
}

export type FactorType = "totp" | "otp_sms" | "otp_email" | "u2f";

export interface SecurityFactor {
  type: FactorType;
  state: string;
  ready: boolean;
  id?: string | null;
  name?: string | null;
}

export interface SecurityInfo {
  factors: SecurityFactor[];
  /** Second factors the org's Zitadel login policy lets a user enrol. */
  allowed_second_factors: FactorType[];
  force_mfa: boolean;
  passwordless_allowed: boolean;
  /** Zitadel console fallback (unused when u2f_in_app is true). */
  console_url: string;
  /** Security keys can be enrolled in-app (login service account configured). */
  u2f_in_app: boolean;
  rp_domain: string;
}

export interface U2FBegin {
  u2f_id: string;
  /** PublicKeyCredentialCreationOptions JSON (base64url fields) */
  options: any;
  rp_domain: string;
}

export interface TotpSetup {
  secret: string;
  uri: string;
}

export interface MessageResp {
  message: string;
}

export interface ProfileUpdate {
  first_name: string;
  last_name: string;
  display_name?: string;
}

/* =====================
   API
===================== */

export const accountApi = {
  get: () => authzInstance.get<Account>("/auth/me/account"),

  updateProfile: (data: ProfileUpdate) =>
    authzInstance.put<Account>("/auth/me/profile", data),

  // Email is the login identity and is set by an administrator; users can
  // only verify it (no changeEmail on purpose).
  verifyEmail: (code: string) =>
    authzInstance.post<Account>("/auth/me/email/verify", { code }),
  resendEmailCode: () =>
    authzInstance.post<MessageResp>("/auth/me/email/resend"),

  changePhone: (phone: string) =>
    authzInstance.put<MessageResp>("/auth/me/phone", { phone }),
  verifyPhone: (code: string) =>
    authzInstance.post<Account>("/auth/me/phone/verify", { code }),
  resendPhoneCode: () =>
    authzInstance.post<MessageResp>("/auth/me/phone/resend"),
  removePhone: () => authzInstance.delete<Account>("/auth/me/phone"),

  changePassword: (old_password: string, new_password: string) =>
    authzInstance.put<MessageResp>("/auth/me/password", {
      old_password,
      new_password,
    }),

  security: () => authzInstance.get<SecurityInfo>("/auth/me/security"),

  totpBegin: () => authzInstance.post<TotpSetup>("/auth/me/factors/totp"),
  totpVerify: (code: string) =>
    authzInstance.post<MessageResp>("/auth/me/factors/totp/verify", { code }),
  totpRemove: () => authzInstance.delete<MessageResp>("/auth/me/factors/totp"),

  otpSmsAdd: () => authzInstance.post<MessageResp>("/auth/me/factors/otp-sms"),
  otpSmsRemove: () =>
    authzInstance.delete<MessageResp>("/auth/me/factors/otp-sms"),

  otpEmailAdd: () =>
    authzInstance.post<MessageResp>("/auth/me/factors/otp-email"),
  otpEmailRemove: () =>
    authzInstance.delete<MessageResp>("/auth/me/factors/otp-email"),

  u2fBegin: () => authzInstance.post<U2FBegin>("/auth/me/factors/u2f"),
  u2fVerify: (u2f_id: string, name: string, credential: any) =>
    authzInstance.post<MessageResp>("/auth/me/factors/u2f/verify", { u2f_id, name, credential }),
  u2fRemove: (tokenId: string) =>
    authzInstance.delete<MessageResp>(
      `/auth/me/factors/u2f/${encodeURIComponent(tokenId)}`,
    ),

  /**
   * Re-read /auth/userinfo after a profile change so the sidebar, Chatwoot
   * and role helpers (all fed from localStorage "user_info") pick it up.
   */
  refreshUserInfo: async () => {
    const r = await authzInstance.get("/auth/userinfo", {
      withCredentials: true,
    });
    localStorage.setItem("user_info", JSON.stringify(r.data));
    return r.data;
  },
};

/** Human-readable text from an authz-api / Zitadel error. */
export function accountErr(err: any, fallback: string): string {
  if (err?.message === "Session expired") return "Session expired";
  const d = err?.response?.data?.detail;
  if (!d) return err?.message && !err?.response ? "Network error" : fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    // pydantic validation errors
    const msgs = d
      .map((e: any) => (typeof e?.msg === "string" ? e.msg.replace(/^Value error, /, "") : ""))
      .filter(Boolean);
    return msgs.length ? msgs.join(". ") : fallback;
  }
  if (typeof d === "object" && typeof d.message === "string") return d.message;
  return fallback;
}
